/* eslint-disable object-curly-newline */
/* eslint-disable comma-dangle */
/* eslint-disable quotes */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Select, Button } from "@mantine/core";
import { FiDownload, FiSearch, FiInfo, FiAlertCircle } from "react-icons/fi";
import { IconFileSpreadsheet } from "@tabler/icons-react";
import { pasqyraFinanciareATK } from "../../../../services/fetch-service";

const TEKSTI_I_ZBEHTE = "#6b7280";
const NGJYRA_EXCEL = "#1d6f42";

const VITI_MINIMAL = 2000;

// Kodet qe ATK-ja i pranon per formNo, ne rendin e dokumentacionit: 100-shet jane
// pasqyra individuale, 150-shet homologet e tyre te konsoliduara.
const FORMULARET = ["Fs100", "Fs110", "Fs120", "Fs130", "Fs150", "Fs160", "Fs170", "Fs180"];

const emriNgaHeaderi = (cd) => {
    const perputhja = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd || "");
    if (!perputhja) return "";
    try {
        return decodeURIComponent(perputhja[1].trim());
    } catch (e) {
        return perputhja[1].trim();
    }
};

const formatoMadhesine = (bajte) => {
    if (!bajte || bajte < 1024) return `${bajte || 0} B`;
    if (bajte < 1024 * 1024) return `${(bajte / 1024).toFixed(1)} KB`;
    return `${(bajte / (1024 * 1024)).toFixed(1)} MB`;
};

const ruajSkedarin = (blob, emri) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = emri;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);
};

const Njoftimi = ({ mesazhi, gabim }) => (
    <div
        style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "14px 16px",
            background: gabim ? "#fdecea" : "#fff8e6",
            border: "1px solid " + (gabim ? "#f5c2bc" : "#f0d9a8"),
            borderRadius: 4,
            color: gabim ? "#a93226" : "#8a6d1f",
            fontSize: 14,
            lineHeight: 1.5,
        }}
    >
        {gabim ? (
            <FiAlertCircle size={17} style={{ marginTop: 2, flexShrink: 0 }} />
        ) : (
            <FiInfo size={17} style={{ marginTop: 2, flexShrink: 0 }} />
        )}
        <span>{mesazhi}</span>
    </div>
);

Njoftimi.propTypes = {
    mesazhi: PropTypes.string.isRequired,
    gabim: PropTypes.bool,
};

// Perdoruesi zgjedh vitin dhe formularin, ATK-ja e gjeneron pasqyren si Excel dhe
// ketu shfaqet skedari i gatshem per shkarkim.
const AtkPasqyratFinanciare = ({ token, L }) => {
    const vitiAktual = new Date().getFullYear();
    // Pasqyra e vitit aktual deklarohet vitin e ardhshem, prandaj parazgjedhja eshte viti i kaluar.
    const [viti, setViti] = useState(String(Math.max(VITI_MINIMAL, vitiAktual - 1)));
    const [formulari, setFormulari] = useState(FORMULARET[0]);
    // "gati" | "duke-kerkuar" | "u-gjet" | "bosh" | "gabim"
    const [statusi, setStatusi] = useState("gati");
    const [mesazhiGabimit, setMesazhiGabimit] = useState("");
    const [skedari, setSkedari] = useState(null);

    // Cdo kerkese merr nje numer; pergjigjet e vonuara (pas nderrimit te zgjedhjes
    // ose mbylljes se tabit) injorohen, qe karta te perputhet gjithmone me zgjedhjen.
    const kerkesaRef = useRef(0);
    useEffect(() => () => {
        kerkesaRef.current += 1;
    }, []);

    const vitet = useMemo(() => {
        const lista = [];
        for (let v = vitiAktual; v >= VITI_MINIMAL; v -= 1) lista.push({ value: String(v), label: String(v) });
        return lista;
    }, [vitiAktual]);

    const formularet = useMemo(
        () => FORMULARET.map((kodi) => ({ value: kodi, label: `${kodi.slice(2)} – ${L.formularet[kodi]}` })),
        [L]
    );

    const pastroRezultatin = () => {
        kerkesaRef.current += 1;
        setStatusi("gati");
        setSkedari(null);
        setMesazhiGabimit("");
    };

    const ndryshoVitin = (v) => {
        if (!v) return;
        setViti(v);
        pastroRezultatin();
    };

    const ndryshoFormularin = (f) => {
        if (!f) return;
        setFormulari(f);
        pastroRezultatin();
    };

    const kerko = async () => {
        if (!token || !viti || !formulari) return;

        kerkesaRef.current += 1;
        const kjoKerkese = kerkesaRef.current;
        setStatusi("duke-kerkuar");
        setSkedari(null);
        setMesazhiGabimit("");

        try {
            const res = await pasqyraFinanciareATK(token, viti, formulari);
            if (kjoKerkese !== kerkesaRef.current) return;

            if (res.ok) {
                const blob = await res.blob();
                if (kjoKerkese !== kerkesaRef.current) return;
                const emri = emriNgaHeaderi(res.headers.get("Content-Disposition")) || `FSR_${viti}_${formulari}.xlsx`;
                setSkedari({ blob, emri, viti, formulari });
                setStatusi("u-gjet");
                return;
            }

            // 404 = biznesi s'e ka deklaruar kete pasqyre per kete vit (s'eshte gabim).
            if (res.status === 404) {
                setStatusi("bosh");
                return;
            }

            if (res.status === 401) setMesazhiGabimit(L.sesioniSkadoi);
            else if (res.status === 429) setMesazhiGabimit(L.shumeKerkesa);
            else setMesazhiGabimit(L.pasqyraGabim);
            setStatusi("gabim");
        } catch (e) {
            if (kjoKerkese !== kerkesaRef.current) return;
            setMesazhiGabimit(L.pasqyraGabim);
            setStatusi("gabim");
        }
    };

    return (
        <div className="content-details">
            <h4>{L.pasqyratTitulli}</h4>
            <div
                style={{
                    display: "grid",
                    gap: 18,
                    padding: 20,
                    border: "1px solid rgba(0, 0, 0, 0.025)",
                    boxShadow: "0px 5px 15px 0 rgb(0 0 0 / 10%)",
                    borderRadius: 2,
                    minWidth: 0,
                }}
            >
                <p style={{ margin: 0, color: TEKSTI_I_ZBEHTE, fontSize: 14 }}>{L.pasqyratPershkrim}</p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
                    <Select
                        label={L.viti}
                        data={vitet}
                        value={viti}
                        onChange={ndryshoVitin}
                        maxDropdownHeight={260}
                        withinPortal
                        style={{ flex: "0 0 130px" }}
                    />
                    <Select
                        label={L.formulari}
                        data={formularet}
                        value={formulari}
                        onChange={ndryshoFormularin}
                        maxDropdownHeight={320}
                        withinPortal
                        style={{ flex: "1 1 320px", minWidth: 0 }}
                    />
                    <Button
                        leftIcon={<FiSearch size={14} />}
                        onClick={kerko}
                        loading={statusi === "duke-kerkuar"}
                        disabled={!token}
                    >
                        {L.kerko}
                    </Button>
                </div>

                {statusi === "u-gjet" && skedari ? (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 14,
                            padding: "14px 16px",
                            background: "#f8faff",
                            border: "1px solid #d7e3f7",
                            borderRadius: 8,
                        }}
                    >
                        <span
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 8,
                                background: "#e7f4ec",
                                color: NGJYRA_EXCEL,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                            aria-hidden
                        >
                            <IconFileSpreadsheet size={24} />
                        </span>
                        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                            <div style={{ fontWeight: 600, wordBreak: "break-all" }}>{skedari.emri}</div>
                            <div style={{ fontSize: 13, color: TEKSTI_I_ZBEHTE }}>
                                {`${skedari.formulari.slice(2)} – ${L.formularet[skedari.formulari]} · ${skedari.viti} · ${formatoMadhesine(skedari.blob.size)}`}
                            </div>
                        </div>
                        <Button
                            color="green"
                            leftIcon={<FiDownload size={14} />}
                            onClick={() => ruajSkedarin(skedari.blob, skedari.emri)}
                        >
                            {L.shkarko}
                        </Button>
                    </div>
                ) : null}

                {statusi === "bosh" ? <Njoftimi mesazhi={L.pasqyraBosh} /> : null}
                {statusi === "gabim" ? <Njoftimi mesazhi={mesazhiGabimit || L.pasqyraGabim} gabim /> : null}
            </div>
        </div>
    );
};

AtkPasqyratFinanciare.propTypes = {
    token: PropTypes.string,
    L: PropTypes.object.isRequired,
};

export default AtkPasqyratFinanciare;
